import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, Upload, Mail, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function ProfileEditor() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", bio: "" });
  const [photo, setPhoto] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [demo, setDemo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name || "", email: user.email || "", address: user.address || "", city: user.city || "", bio: user.bio || "" });
    setEmailVerified(!!user.email_verified);
    if (user.photo_id) api.get(`/files/${user.photo_id}`, { responseType: "blob" }).then((r) => setPhoto(URL.createObjectURL(r.data))).catch(() => {});
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { toast.error("File too large. Files more than 500KB are not permissible."); return; }
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/profile", { photo_id: res.data.id });
      setPhoto(URL.createObjectURL(f));
      await refresh();
      toast.success("Photo updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };

  const sendCode = async () => {
    if (!form.email) return toast.error("Enter your email first");
    try {
      const r = await api.post("/email/otp/start", { email: form.email });
      setSent(true); setDemo(r.data.demo_code || "");
      toast.success("Verification code sent to your email");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send code"); }
  };
  const verifyCode = async () => {
    try {
      await api.post("/email/otp/verify", { email: form.email, otp });
      setEmailVerified(true); setSent(false);
      await refresh();
      toast.success("Email verified");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { name: form.name, address: form.address, city: form.city, bio: form.bio });
      await refresh();
      toast.success("Profile saved");
    } catch (e) { toast.error("Could not save"); }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="profile-editor">
      <h3 className="font-display text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><User className="h-5 w-5 text-teal" /> Personal information</h3>
      <div className="flex items-center gap-4 mb-5">
        <div className="h-16 w-16 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400 font-bold text-xl">
          {photo ? <img src={photo} alt="profile" className="h-full w-full object-cover" /> : (form.name?.[0] || "U")}
        </div>
        <label className="inline-flex items-center gap-2 rounded-lg border border-teal-brand bg-teal-brand/5 text-teal font-medium text-sm px-3 py-2 cursor-pointer hover:bg-teal-brand/10" data-testid="profile-photo-label">
          <Upload className="h-4 w-4" /> Add / change photo
          <input type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={uploadPhoto} data-testid="profile-photo-input" />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Full name</Label><Input value={form.name} onChange={set("name")} data-testid="profile-name" /></div>
        <div><Label>Account type</Label><Input value={user?.user_type || ""} disabled data-testid="profile-usertype" /></div>
        <div className="sm:col-span-2">
          <Label>Email {emailVerified && <span className="text-emerald-600 text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> verified</span>}</Label>
          <div className="flex gap-2">
            <Input value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setEmailVerified(false); }} data-testid="profile-email" placeholder="you@example.com" />
            {!emailVerified && <Button variant="outline" onClick={sendCode} data-testid="profile-send-code"><Mail className="h-4 w-4 mr-1" /> Verify</Button>}
          </div>
          {sent && !emailVerified && (
            <div className="mt-2 flex gap-2 items-center">
              {demo && <span className="text-xs text-amber-700">Demo code: <b className="font-mono">{demo}</b></span>}
              <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="w-40" data-testid="profile-email-otp" />
              <Button onClick={verifyCode} className="bg-teal-brand text-white" data-testid="profile-verify-code">Confirm</Button>
            </div>
          )}
        </div>
        <div><Label>City</Label><Input value={form.city} onChange={set("city")} data-testid="profile-city" /></div>
        <div><Label>Address</Label><Input value={form.address} onChange={set("address")} data-testid="profile-address" /></div>
        <div className="sm:col-span-2"><Label>About / other info</Label><Textarea rows={2} value={form.bio} onChange={set("bio")} data-testid="profile-bio" /></div>
      </div>
      <Button className="mt-4 bg-teal-brand hover:bg-teal-700 text-white" onClick={save} disabled={saving} data-testid="profile-save">{saving ? "Saving…" : "Save profile"}</Button>
    </div>
  );
}
